import type { Money, OrderStatus } from './types';

/** Format a decimal-string Money as ₹ (INR) or a generic currency prefix. */
export function formatMoney(m?: Money): string {
  if (!m || m.value === '') return '—';
  if (!m.currency || m.currency === 'INR') return `₹${m.value}`;
  return `${m.currency} ${m.value}`;
}

/** Format an ISO timestamp in the viewer's local timezone. */
export function formatDateTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format an ISO timestamp with the year, for receipts/validity (e.g. "09 Jul 2026, 12:18 PM").
 * The compact formatDateTime drops the year, which reads ambiguously on a ticket receipt.
 */
export function formatDateTimeFull(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Format an ISO timestamp as a date (no time). */
export function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// P[nY][nMo][nW][nD] T [nH][nMi][nS] — the month is the `M` before `T`, the minute
// the one after. Fractional seconds (…S) are permitted and discarded.
const ISO_DURATION =
  /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

/**
 * Parse the ONDC `item.time.duration` — an ISO 8601 duration (e.g. "PT12H28M58.777S",
 * "P3D", "P1DT2H") the backend forwards verbatim — into a clean, compact label
 * ("12h 28m", "3d"). Returns '' for empty/unparseable input, an effectively-zero
 * duration, or a years/months value (not a transit validity window) — the caller
 * then hides the validity chip rather than showing a raw, ugly string.
 */
export function formatValidityDuration(raw?: string): string {
  if (!raw) return '';
  const m = ISO_DURATION.exec(raw.trim());
  if (!m) return '';
  const n = (i: number) => (m[i] ? parseInt(m[i], 10) : 0);
  // Years/months don't occur for a metro/bus/pass validity window; if they somehow
  // appear, bail rather than render a misleading approximation.
  if (n(1) > 0 || n(2) > 0) return '';
  const days = n(4) + n(3) * 7; // weeks fold into days
  const hours = n(5);
  const minutes = n(6);
  const seconds = m[7] ? parseFloat(m[7]) : 0;
  const parts = [
    ...(days > 0 ? [`${days}d`] : []),
    ...(hours > 0 ? [`${hours}h`] : []),
    ...(minutes > 0 ? [`${minutes}m`] : []),
  ];
  if (parts.length === 0) return seconds > 0 ? '<1m' : '';
  // Two most-significant units keep the label tidy (e.g. "2d 3h", not "2d 3h 40m").
  return parts.slice(0, 2).join(' ');
}

export type BadgeVariant = 'default' | 'primary' | 'green' | 'blue' | 'amber' | 'red' | 'purple';

/** Map an order/ticket status to a Badge variant. */
export function statusVariant(status: OrderStatus | string): BadgeVariant {
  switch (status) {
    case 'ACTIVE':
      return 'primary';
    case 'COMPLETED':
      return 'green';
    case 'CANCELLED':
      return 'red';
    // CANCEL_INITIATED is a cancellation the operator accepted but has not finalised. Amber, like a
    // soft cancel: in-flight, not terminal. Without it the status fell through to the neutral
    // default and the badge rendered the raw enum string.
    case 'SOFT_CANCEL':
    case 'CANCEL_INITIATED':
      return 'amber';
    default:
      return 'default';
  }
}

/** Map a pass ticketStatus (UNCLAIMED/CLAIMED) to a Badge variant. */
export function passStatusVariant(status?: string): BadgeVariant {
  switch (status) {
    case 'CLAIMED':
      return 'green';
    case 'UNCLAIMED':
      return 'amber';
    default:
      return 'default';
  }
}

// --- Ticket types (ONDC TRV11 fare products) -----------------------------------
// The backend forwards the operator's raw descriptor code (SJT/SFSJT/RJT/PASS);
// these turn it into plain language so a rider knows what they're buying.

/** Short, human label for a ticket-type code. Falls back to the raw code. */
export function ticketTypeLabel(code?: string): string {
  switch (code) {
    case 'SJT':
      return 'Single journey';
    case 'RJT':
      return 'Return journey';
    case 'SFSJT':
      return 'Special-fare single journey';
    case 'PASS':
      return 'Travel pass';
    default:
      return code ?? '';
  }
}

/** One-line explanation of what a ticket type gets you. '' for unknown codes. */
export function ticketTypeDescription(code?: string): string {
  switch (code) {
    case 'SJT':
      return 'One trip from your start station to your destination.';
    case 'RJT':
      return 'A round trip — travel there and back on the same ticket.';
    case 'SFSJT':
      return 'A single trip at a special or concessional fare.';
    case 'PASS':
      return 'Multiple journeys within a set validity period (e.g. a day pass).';
    default:
      return '';
  }
}

/** Friendly label for a pass/QR status (UNCLAIMED → ready, CLAIMED → used). */
export function ticketStatusLabel(status?: string): string {
  switch (status) {
    case 'UNCLAIMED':
      return 'Ready to use';
    case 'CLAIMED':
      return 'Used';
    default:
      return status ?? '';
  }
}

/** Friendly label for an order status. */
export function orderStatusLabel(status: OrderStatus | string): string {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'SOFT_CANCEL':
      return 'Cancelling';
    default:
      return String(status);
  }
}

// A plain-language note shown on the booking screen. Metro/bus operators only sell
// tickets during service hours, so a late-night search legitimately returns nothing.
//
// This is the FALLBACK, used only when we don't hold this operator's published hours —
// `serviceHoursNote()` states the real window when we do. It deliberately quotes no times:
// the previous "most lines run roughly 5 AM–11 PM" was wrong for BMRCL (04:30–02:30) and
// unverified for everyone else.
export const SERVICE_HOURS_NOTE =
  "Metro and bus tickets can only be bought during the operator's service hours, and are valid for same-day travel.";

// Common ONDC transit error codes → a rider-friendly explanation. Used to soften the
// raw operator/Beckn messages that otherwise surface as codes or terse strings.
const TRANSIT_ERROR_COPY: Record<string, string> = {
  '91201':
    "We couldn't find a route between these stations. Try a different pair or another operator.",
  '91202': 'The starting station isn’t served right now. Try another station or operator.',
  '91203': 'The destination isn’t served right now. Try another station or operator.',
  '91204': 'That’s more tickets than this operator allows in one booking. Try a smaller quantity.',
  '91206':
    'The operator is temporarily unavailable. Please try again in a few minutes, or try another operator.',
  '91207': 'The operator couldn’t complete this request. Please try again.',
  '91208':
    'This service is outside its operating hours right now. Please try again during service hours.',
  '91210': 'The operator couldn’t load its stations right now. Please try again shortly.',
  '91211': 'The operator couldn’t fetch the fare right now. Please try again.',
  // No passenger cap here: this map has no operator context, and the cap is per-operator.
  // The booking screen states the real number from the operator policy.
  '91215':
    'You can have only one active ticket at a time. Use or cancel your current ticket first.',
  '91216': 'This ticket has already been used and can’t be booked again.',
  '91217': 'This payment reference was already used for another booking. Please try again.',
  '30008': 'This location isn’t serviceable. Try a station within the metro/bus network.',
};

/**
 * Turn a transit error (ONDC code + message) into rider-friendly copy.
 * Returns the mapped copy for a known code, else the operator's message, else a
 * generic fallback.
 */
export function friendlyTransitError(err?: { code?: string; message?: string }): string {
  const mapped = err?.code ? TRANSIT_ERROR_COPY[err.code] : undefined;
  if (mapped) return mapped;
  if (err?.message && err.message.trim()) return err.message;
  return 'Something went wrong with the operator. Please try again, or try another operator.';
}
